import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCreditCard, FaExclamationTriangle } from 'react-icons/fa';
import { FaClock, FaExclamation, FaCheck } from 'react-icons/fa6';
import { TbPlus, TbExternalLink, TbCloudDownload } from 'react-icons/tb';
import { CgSpinner } from 'react-icons/cg';
import WithTooltip from '../misc/WithTooltip';
import Button from '../buttons/Button';
import { BankAccount } from '../../../types/bankAccount';
import { formatEuroCents } from '../../../utils/bankAccountUtil';

interface FinancialMetrics {
  totalBalance: number;
  totalIncome: number;
  totalExpenses: number;
  unprocessedMutations: number;
  connectedAccounts: number;
  disconnectedAccounts: number;
}

interface BankAccountsCardProps {
  bankAccounts: BankAccount[];
  metrics: Pick<FinancialMetrics, 'connectedAccounts' | 'disconnectedAccounts'>;
  unprocessedByAccount?: Record<number, number>;
  onCreateAccount: () => void;
  onAccountClick: (accountId: number) => void;
  onStartSync?: (accountId: number) => Promise<void> | void;
}

export default function BankAccountsCard({
  bankAccounts,
  metrics,
  unprocessedByAccount,
  onCreateAccount,
  onAccountClick,
  onStartSync,
}: BankAccountsCardProps): JSX.Element {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.overview' });
  const [syncingId, setSyncingId] = React.useState<number | null>(null);

  const getConnectionStatus = (account: BankAccount) => {
    if (!account.requisitionId) {
      return {
        status: 'disconnected',
        label: t('accounts.status.notConnected'),
        icon: <FaExclamation className="text-secondary" />,
        color: 'text-secondary',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
      };
    }

    if (!account.transferCompleted) {
      return {
        status: 'failed',
        label: t('accounts.status.failed'),
        icon: <FaExclamation className="text-danger" />,
        color: 'text-danger',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
      };
    }

    if (account.requisitionExpiryDate && new Date() > account.requisitionExpiryDate) {
      return {
        status: 'expired',
        label: t('accounts.status.expired'),
        icon: <FaClock className="text-danger" />,
        color: 'text-danger',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
      };
    }

    return {
      status: 'connected',
      label: t('accounts.status.connected'),
      icon: <FaCheck className="text-success" />,
      color: 'text-success',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
    };
  };

  const hasUnprocessedTransactions = (account: BankAccount) => {
    const count = unprocessedByAccount?.[account.accountId] ?? 0;
    return count > 0;
  };

  const isSameDayLocal = (a: Date, b: Date) => {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const getLastUpdatedText = (account: BankAccount) => {
    if (!account.latestFetching) return t('accounts.lastUpdated.never');
    const lastFetchingDate =
      typeof account.latestFetching === 'string'
        ? new Date(account.latestFetching)
        : account.latestFetching;
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - lastFetchingDate.getTime()) / (1000 * 60));
    if (diffInMinutes < 1) return t('accounts.lastUpdated.lessThanMinute');
    if (diffInMinutes < 60)
      return t('accounts.lastUpdated.minutes', {
        count: diffInMinutes,
        plural: diffInMinutes > 1 ? 's' : '',
      });
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return t('accounts.lastUpdated.hours', {
        count: diffInHours,
        plural: diffInHours > 1 ? 's' : '',
      });
    const diffInDays = Math.floor(diffInHours / 24);
    return t('accounts.lastUpdated.days', {
      count: diffInDays,
      plural: diffInDays > 1 ? 's' : '',
    });
  };

  // removed same-day gating; using unprocessedByAccount instead

  const getAccountTypeLabel = (account: BankAccount) => {
    const accountName = account.accountName.toLowerCase();
    if (accountName.includes('spaar') || accountName.includes('savings')) return 'Savings';
    if (accountName.includes('business') || accountName.includes('zakelijk')) return 'Business';
    return 'Checking';
  };

  const getBankLogo = (bank: string) => {
    const bankLogos: Record<string, string> = {
      RABOBANK: '/assets/banks/rabobank.svg',
      ABN_AMRO: '/assets/banks/abn-amro.svg',
      ING: '/assets/banks/ing.svg',
      ASN_BANK: '/assets/banks/asn-bank.svg',
      BUNQ: '/assets/banks/bunq.svg',
      SNS_BANK: '/assets/banks/sns-bank.svg',
    };
    return bankLogos[bank] || '/assets/banks/default-bank.svg';
  };

  return (
    <div className="rounded-xl border border-secondary-200 bg-white shadow-sm">
      <div className="border-secondary-100 flex items-center justify-between border-b p-6">
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">{t('accounts.title')}</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            {t('accounts.summary', {
              connected: metrics.connectedAccounts,
              disconnected: metrics.disconnectedAccounts,
            })}
          </p>
        </div>
      </div>

      <div className="p-6">
        {bankAccounts.length === 0 ? (
          <div className="py-8 text-center">
            <FaCreditCard className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-medium text-gray-900">{t('accounts.empty.title')}</h3>
            <p className="mt-2 text-sm text-gray-500">{t('accounts.empty.subtitle')}</p>
            <div className="mt-4">
              <Button variant="primary-dark" onClick={onCreateAccount}>
                <TbPlus />
                {t('accounts.empty.addAccount')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {bankAccounts.map((account) => {
              const status = getConnectionStatus(account);
              const hasUnprocessed = hasUnprocessedTransactions(account);
              const unprocessedCount = unprocessedByAccount?.[account.accountId] ?? 0;
              const accountType = getAccountTypeLabel(account);
              const lastUpdated = getLastUpdatedText(account);

              return (
                <div
                  key={account.accountId}
                  className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-4 transition-colors hover:bg-gray-50"
                  onClick={() => onAccountClick(account.accountId)}>
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-blue-500 bg-white">
                      <img
                        src={getBankLogo(account.bank as unknown as string)}
                        alt={`${account.bank} logo`}
                        className="h-6 w-6 object-contain"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/banks/default-bank.svg';
                        }}
                      />
                    </div>

                    <div className="flex-1">
                      <div className="mb-1 flex items-center space-x-2">
                        <p className="font-semibold text-gray-900">{account.bank}</p>
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-primary">
                          {accountType}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span className="font-mono">...{account.iban.slice(-4)}</span>
                        <span>·</span>
                        <span>{lastUpdated}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {hasUnprocessed && (
                      <WithTooltip
                        className="w-72 whitespace-normal text-center"
                        tooltip={t('accounts.unprocessedTooltip', {
                          count: unprocessedCount,
                          defaultValue: '{{count}} unprocessed mutation(s) awaiting processing.',
                        })}>
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100">
                          <FaExclamationTriangle className="h-4 w-4 text-secondary" />
                        </div>
                      </WithTooltip>
                    )}

                    <div
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${status.bgColor} ${status.color}`}>
                      {status.icon}
                      <span className="ml-1">{status.label}</span>
                    </div>

                    {status.status !== 'connected' && (
                      <Button
                        variant="primary-dark"
                        className="px-2 py-1 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStartSync?.(account.accountId);
                        }}>
                        {t('linkButton', { defaultValue: 'Connect' })}
                      </Button>
                    )}

                    {status.status === 'connected' &&
                      (!account.latestFetching ||
                        !isSameDayLocal(
                          typeof account.latestFetching === 'string'
                            ? new Date(account.latestFetching)
                            : account.latestFetching,
                          new Date(),
                        )) && (
                        <Button
                          variant="secondary"
                          className="px-2 py-1 text-xs"
                          disabled={syncingId === account.accountId}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!onStartSync || syncingId === account.accountId) return;
                            try {
                              setSyncingId(account.accountId);
                              await onStartSync(account.accountId);
                            } finally {
                              setSyncingId((prev) => (prev === account.accountId ? null : prev));
                            }
                          }}>
                          {syncingId === account.accountId ? (
                            <CgSpinner className="animate-spin" />
                          ) : (
                            <TbCloudDownload />
                          )}
                          {syncingId === account.accountId
                            ? t('accounts.syncing', { defaultValue: 'Syncing...' })
                            : t('accounts.sync', { defaultValue: 'Sync' })}
                        </Button>
                      )}

                    <div className="text-right">
                      <p className="text-lg font-semibold text-gray-900">
                        {formatEuroCents(account.balance, i18n.language)}
                      </p>
                    </div>

                    <div className="flex items-center text-sm text-gray-500">
                      <span>{t('accounts.details')}</span>
                      <TbExternalLink className="ml-1 h-4 w-4" />
                    </div>
                    {null}
                  </div>
                </div>
              );
            })}
            <div className="text-center">
              <Button variant="primary-dark" onClick={onCreateAccount}>
                <TbPlus />
                {t('accounts.empty.addAccount')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
